import { useRequiredContext } from "@redotech/react-util/context";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { SupportFolder } from "@redotech/redo-support-common/support-folder";
import { toast } from "@redotech/redo-web/alert";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const supportFolderKeys = {
  all: (teamId: string) => ["supportFolders", teamId] as const,
  folder: (teamId: string, folderId: string) =>
    ["supportFolders", teamId, folderId] as const,
};

export function useSupportFoldersQuery() {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);

  return useQuery({
    queryKey: supportFolderKeys.all(team._id),
    queryFn: async () => {
      const result = await rpcClient.getSupportFolders({});
      return result.folders;
    },
  });
}

export function useSupportFolderMutations() {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const queryClient = useQueryClient();

  const createFolder = useMutation({
    mutationFn: async (data: {
      name: string;
      viewIds: string[];
      defaultViews: string[];
      isPrivate: boolean;
    }) => {
      return rpcClient.createSupportFolder(data);
    },
    onSuccess: async (newFolder) => {
      queryClient.setQueryData<SupportFolder[]>(
        supportFolderKeys.all(team._id),
        (old) => (old ? [...old, newFolder] : [newFolder]),
      );
    },
    onError: (error: Error) => {
      toast(`Failed to create folder: ${error.message}`, { variant: "error" });
    },
  });

  const updateFolder = useMutation({
    mutationFn: async (data: {
      folderId: string;
      name?: string;
      viewIds?: string[];
      defaultViews?: string[];
    }) => {
      return rpcClient.updateSupportFolder(data);
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({
        queryKey: supportFolderKeys.all(team._id),
      });

      const previousFolders = queryClient.getQueryData<SupportFolder[]>(
        supportFolderKeys.all(team._id),
      );

      queryClient.setQueryData<SupportFolder[]>(
        supportFolderKeys.all(team._id),
        (old) =>
          old?.map((folder) =>
            folder._id === updates.folderId
              ? { ...folder, ...updates }
              : folder,
          ),
      );

      return { previousFolders };
    },
    onSuccess: async (updatedFolder) => {
      if (updatedFolder) {
        queryClient.setQueryData<SupportFolder[]>(
          supportFolderKeys.all(team._id),
          (old) =>
            old?.map((folder) =>
              folder._id === updatedFolder._id ? updatedFolder : folder,
            ),
        );
      }
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousFolders) {
        queryClient.setQueryData(
          supportFolderKeys.all(team._id),
          context.previousFolders,
        );
      }
      toast(`Failed to update folder: ${error.message}`, { variant: "error" });
    },
  });

  const deleteFolder = useMutation({
    mutationFn: async (folderId: string) => {
      return rpcClient.deleteSupportFolder({ folderId });
    },
    onMutate: async (folderId) => {
      await queryClient.cancelQueries({
        queryKey: supportFolderKeys.all(team._id),
      });

      const previousFolders = queryClient.getQueryData<SupportFolder[]>(
        supportFolderKeys.all(team._id),
      );

      queryClient.setQueryData<SupportFolder[]>(
        supportFolderKeys.all(team._id),
        (old) => old?.filter((folder) => folder._id !== folderId),
      );

      return { previousFolders };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousFolders) {
        queryClient.setQueryData(
          supportFolderKeys.all(team._id),
          context.previousFolders,
        );
      }
      toast(`Failed to delete folder: ${error.message}`, { variant: "error" });
    },
  });

  return { createFolder, updateFolder, deleteFolder };
}
